import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  doc, onSnapshot, collection, collectionGroup, query,
  where, getDocs, deleteDoc, setDoc, serverTimestamp
} from 'firebase/firestore'
import {
  signInWithEmailAndPassword, createUserWithEmailAndPassword,
  sendPasswordResetEmail, signInWithPopup, signInWithCredential, GoogleAuthProvider,
  EmailAuthProvider, reauthenticateWithCredential, deleteUser, updateProfile
} from 'firebase/auth'
import { db, auth } from '../firebase/config'
import {
  User, Award, Heart, Music, TrendingUp, Calendar, Clock, Flame,
  Settings, AlertTriangle, Trash2, LogOut, Mail, Lock, UserPlus,
  Eye, EyeOff, KeyRound, Star, CreditCard, CheckCircle, XCircle,
  AlertCircle, X, Wallet
} from 'lucide-react'
import { toast } from '../hooks/useToast'
import { Capacitor } from '@capacitor/core'
import { FirebaseAuthentication, isNativeGoogleAvailable } from '../utils/nativeGoogleAuth'
import { VERSION_LABEL } from '../utils/appPrefs'
import './ProfilePage.css'


// ── Icône Google ─────────────────────────────────────────
const GoogleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
)

/**
 * Connexion Google.
 * - Application Android : fenêtre native "Choisir un compte" (plugin
 *   @capacitor-firebase/authentication). La fenêtre popup du web ne
 *   fonctionne pas dans une app : c'est pour ça qu'elle échouait.
 * - Navigateur : popup Google classique.
 * Renvoie l'utilisateur Firebase connecté, ou null si annulé.
 */
async function signInWithGoogleAnywhere() {
  if (Capacitor.isNativePlatform()) {
    if (!isNativeGoogleAvailable()) {
      const err = new Error('native-google-missing')
      err.code = 'native-google-missing'
      throw err
    }
    const result = await FirebaseAuthentication.signInWithGoogle()
    const idToken = result?.credential?.idToken
    if (!idToken) return null
    const cred = GoogleAuthProvider.credential(idToken, result?.credential?.accessToken)
    const { user } = await signInWithCredential(auth, cred)
    return user
  }
  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
  const { user } = await signInWithPopup(auth, provider)
  return user
}

const googleErrorMessage = (error) => {
  const raw = `${error?.code || ''} ${error?.message || ''}`.toLowerCase()
  if (raw.includes('cancel') || raw.includes('12501') || raw.includes('popup-closed')) return null
  if (raw.includes('developer_error') || raw.includes(' 10') || raw.includes('10:') || raw.includes('12500')) {
    return "Connexion Google pas encore activée pour l'application (empreinte SHA-1 à ajouter dans Firebase)"
  }
  if (raw.includes('native-google-missing')) {
    return "Connexion Google indisponible dans cette version de l'application (module non installé)"
  }
  if (raw.includes('network')) return 'Pas de connexion internet'
  if (raw.includes('account-exists-with-different-credential')) {
    return 'Un compte existe déjà avec cet e-mail : connectez-vous avec votre mot de passe'
  }
  return 'Connexion Google impossible pour le moment'
}

// ── Badge statut ─────────────────────────────────────────
function SubStatusBadge({ status }) {
  const cfg = {
    active:    { color: '#34d399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.35)',  icon: <CheckCircle size={13}/>, label: 'Active'     },
    completed: { color: '#34d399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.35)',  icon: <CheckCircle size={13}/>, label: 'Actif'      },
    pending:   { color: '#ffd700', bg: 'rgba(255,215,0,0.12)',   border: 'rgba(255,215,0,0.35)',   icon: <AlertCircle size={13}/>, label: 'En attente' },
    expired:   { color: '#ff6b6b', bg: 'rgba(220,20,60,0.12)',   border: 'rgba(220,20,60,0.35)',   icon: <XCircle     size={13}/>, label: 'Expirée'    },
  }
  const c = cfg[status] || cfg.pending
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:5,
      padding:'3px 10px', borderRadius:20, fontSize:'0.7rem', fontWeight:700,
      color:c.color, background:c.bg, border:`1px solid ${c.border}`,
    }}>
      {c.icon}{c.label}
    </span>
  )
}

// ── Helpers ──────────────────────────────────────────────
const fmtDate = (val) => {
  if (!val) return '—'
  const d = val.toDate?.() || new Date(val)
  return d.toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' })
}

// ⚠️ Corrigé : pointait vers "johnny-network", un projet Firebase supprimé.
// walletApi tourne en réalité sur pour100johnny-app, comme le reste du site.
const WALLET_URL = 'https://us-central1-pour100johnny-app.cloudfunctions.net/walletApi'

// Cloud Function "api" (pour100johnny-app) — pont vers AssoRock (adhésions
// gérées sur le projet Firebase séparé mesappsdavid-e1c4c).
const CLOUD_FUNCTIONS_BASE = 'https://us-central1-pour100johnny-app.cloudfunctions.net/api'

// ════════════════════════════════════════════════════════
// MODAL CARTE MEMBRE
// ════════════════════════════════════════════════════════
function CarteMembreModal({ adhesion, currentUser, memberDisplayName, onClose }) {
  const [flipped, setFlipped] = useState(false)
  const [walletLoading, setWalletLoading] = useState(false)

  const nom = memberDisplayName.toUpperCase()

  const cardRefId = adhesion ? (adhesion.memberCardId || adhesion.id) : (currentUser?.uid || '00000000')
  const memberId  = 'JH·' + cardRefId.substring(0, 8).toUpperCase()

  const endDate    = fmtDate(adhesion?.endDate)
  const startDate  = adhesion?.startDate ? (adhesion.startDate.toDate?.() || new Date(adhesion.startDate)) : new Date()
  const endDateObj = adhesion?.endDate   ? (adhesion.endDate.toDate?.()   || new Date(adhesion.endDate))   : new Date()
  const validite   = `${startDate.getFullYear()} — ${endDateObj.getFullYear()}`

  const handleGoogleWallet = async () => {
    if (!adhesion) return
    setWalletLoading(true)
    try {
      const name = nom
      const mid  = 'JH-' + cardRefId.substring(0, 8).toUpperCase()

      // ✅ Ajout du userId pour récupérer les vraies dates depuis Firestore
      const url  = `${WALLET_URL}/google?name=${encodeURIComponent(name)}&memberId=${encodeURIComponent(mid)}&userId=${encodeURIComponent(currentUser.uid)}`

      const resp = await fetch(url)
      const data = await resp.json()
      if (data.saveUrl) {
        window.open(data.saveUrl, '_blank')
        toast.success('✅ Ouverture Google Wallet...')
      } else {
        throw new Error(data.error || 'Erreur inconnue')
      }
    } catch (err) {
      console.error('[Wallet]', err)
      toast.error('❌ Erreur Google Wallet : ' + err.message)
    } finally {
      setWalletLoading(false)
    }
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position:'fixed', inset:0, zIndex:9000,
        background:'rgba(0,0,0,0.88)', backdropFilter:'blur(12px)',
        display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center', padding:'20px',
      }}
    >
      <div style={{
        width:'100%', maxWidth:420,
        background:'#0e0e0e',
        border:'1px solid rgba(201,168,76,0.35)',
        borderRadius:24, padding:'24px 20px',
        display:'flex', flexDirection:'column',
        alignItems:'center', gap:18, position:'relative',
        boxShadow:'0 0 80px rgba(201,168,76,0.1), 0 30px 80px rgba(0,0,0,0.8)',
        maxHeight:'90vh', overflowY:'auto',
      }}>

        {/* Fermer */}
        <button onClick={onClose} style={{
          position:'absolute', top:14, right:14,
          background:'rgba(255,255,255,0.05)',
          border:'1px solid rgba(255,255,255,0.12)',
          borderRadius:'50%', width:34, height:34,
          display:'flex', alignItems:'center', justifyContent:'center',
          cursor:'pointer', color:'rgba(255,255,255,0.5)',
        }}>
          <X size={16}/>
        </button>

        {/* Titre */}
        <div style={{
          fontFamily:"'Bebas Neue', sans-serif",
          fontSize:'1.3rem', letterSpacing:'6px',
          background:'linear-gradient(135deg,#F0D080,#C9A84C)',
          WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent',
          marginTop:4,
        }}>
          MA CARTE MEMBRE
        </div>

        {/* Badge statut */}
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {adhesion ? (
            <>
              <div style={{
                width:8, height:8, borderRadius:'50%', background:'#10b981',
                boxShadow:'0 0 8px rgba(16,185,129,0.8)',
                animation:'pulseDot 2s ease-in-out infinite',
              }}/>
              <span style={{ color:'#10b981', fontSize:'0.78rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'1px' }}>
                Adhésion active · {memberId}
              </span>
            </>
          ) : (
            <>
              <div style={{ width:8, height:8, borderRadius:'50%', background:'#ff6b6b' }}/>
              <span style={{ color:'#ff6b6b', fontSize:'0.78rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'1px' }}>
                Aucune adhésion active
              </span>
            </>
          )}
        </div>

        {/* ── Carte 3D ── */}
        <div style={{ perspective:'1000px', width:'100%', maxWidth:360 }}>
          <div
            onClick={() => setFlipped(f => !f)}
            style={{
              position:'relative', width:'100%', height:210,
              transformStyle:'preserve-3d',
              transition:'transform 0.7s cubic-bezier(0.34,1.56,0.64,1)',
              transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              cursor:'pointer',
              animation: flipped ? 'none' : 'floatCard 6s ease-in-out infinite',
            }}
          >
            {/* RECTO */}
            <div style={{
              position:'absolute', inset:0, borderRadius:16,
              backfaceVisibility:'hidden',
              background:'linear-gradient(135deg,#0d0d0d 0%,#1c1408 45%,#0d0d0d 100%)',
              boxShadow:'0 0 0 1px rgba(201,168,76,0.35), 0 20px 50px rgba(0,0,0,0.85), 0 0 30px rgba(192,40,42,0.12)',
              overflow:'hidden',
            }}>
              <div style={{ position:'absolute', inset:0, background:'linear-gradient(115deg,rgba(192,40,42,0.15) 0%,transparent 35%)', pointerEvents:'none' }}/>
              <div style={{ position:'absolute', inset:4, borderRadius:13, border:'1px solid rgba(201,168,76,0.18)', pointerEvents:'none' }}/>
              <div style={{ position:'absolute', top:10, right:10, background:'rgba(192,40,42,0.85)', border:'1px solid rgba(255,100,100,0.25)', borderRadius:3, padding:'2px 7px', fontSize:'6px', letterSpacing:'2px', color:'rgba(255,255,255,0.9)', textTransform:'uppercase' }}>Fan Officiel</div>
              <div style={{ position:'absolute', top:12, left:'50%', transform:'translateX(-50%)', textAlign:'center' }}>
                <div style={{ fontFamily:"'Bebas Neue', sans-serif", fontSize:22, letterSpacing:5, background:'linear-gradient(180deg,#F0D080 0%,#C9A84C 40%,#8B6914 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', lineHeight:1 }}>100% JOHNNY</div>
                <div style={{ fontSize:6, letterSpacing:3, color:'rgba(201,168,76,0.5)', textTransform:'uppercase', marginTop:1, fontStyle:'italic' }}>Association des Fans Officiels</div>
              </div>
              <div style={{ position:'absolute', top:64, left:'50%', transform:'translateX(-50%)', width:'82%', height:1, background:'linear-gradient(90deg,transparent,rgba(201,168,76,0.4),transparent)' }}>
                <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', background:'#12100a', padding:'0 6px', color:'#C9A84C', fontSize:7 }}>✦</div>
              </div>
              <div style={{ position:'absolute', top:78, right:14, width:34, height:26, borderRadius:4, background:'linear-gradient(135deg,#C9A84C 0%,#F0D080 25%,#C9A84C 50%,#8B6914 75%,#C9A84C 100%)', boxShadow:'0 2px 8px rgba(0,0,0,0.5)', animation:'holoShine 3s ease-in-out infinite' }}/>
              <div style={{ position:'absolute', top:72, left:14, right:60 }}>
                <div style={{ fontSize:6, letterSpacing:4, color:'rgba(201,168,76,0.5)', textTransform:'uppercase', marginBottom:4 }}>Adhérent</div>
                <div style={{ fontFamily:"'Bebas Neue', sans-serif", fontSize:20, letterSpacing:2, color:'#F5EDD6', textShadow:'0 0 20px rgba(201,168,76,0.4)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>★ {nom}</div>
              </div>
              <div style={{ position:'absolute', bottom:34, left:14, right:14, display:'flex', justifyContent:'space-between', alignItems:'flex-end' }}>
                <div>
                  <div style={{ fontSize:6, letterSpacing:3, color:'rgba(201,168,76,0.5)', textTransform:'uppercase', marginBottom:2 }}>Valide jusqu'au</div>
                  <div style={{ fontFamily:"'Bebas Neue', sans-serif", fontSize:14, letterSpacing:1, color:'#F5EDD6' }}>{endDate}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:6, letterSpacing:3, color:'rgba(201,168,76,0.5)', textTransform:'uppercase', marginBottom:2 }}>N° Membre</div>
                  <div style={{ fontFamily:"'Bebas Neue', sans-serif", fontSize:13, letterSpacing:2, background:'linear-gradient(135deg,#F0D080,#C9A84C)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>{memberId}</div>
                </div>
              </div>
              <div style={{ position:'absolute', bottom:0, left:0, right:0, height:30, background:'linear-gradient(90deg,#C0282A,#7a1515 50%,#C0282A)', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 14px', borderRadius:'0 0 16px 16px' }}>
                <span style={{ fontSize:6, letterSpacing:2, color:'rgba(255,255,255,0.7)', textTransform:'uppercase' }}>Carte d'adhérent · 100% Johnny</span>
                <span style={{ fontFamily:"'Bebas Neue', sans-serif", fontSize:11, letterSpacing:1, color:'rgba(255,255,255,0.95)' }}>{validite}</span>
              </div>
            </div>

            {/* VERSO */}
            <div style={{
              position:'absolute', inset:0, borderRadius:16,
              backfaceVisibility:'hidden', transform:'rotateY(180deg)',
              background:'linear-gradient(145deg,#080808 0%,#1a1208 40%,#0d0d10 100%)',
              boxShadow:'0 0 0 1px rgba(201,168,76,0.35), 0 20px 50px rgba(0,0,0,0.85)',
              overflow:'hidden',
            }}>
              <div style={{ position:'absolute', inset:0, opacity:0.04, backgroundImage:'linear-gradient(rgba(201,168,76,1) 1px,transparent 1px),linear-gradient(90deg,rgba(201,168,76,1) 1px,transparent 1px)', backgroundSize:'16px 16px' }}/>
              <div style={{ position:'absolute', left:0, top:0, bottom:0, width:3, background:'linear-gradient(180deg,transparent,#C9A84C 30%,#C0282A 70%,transparent)' }}/>
              <div style={{ position:'absolute', top:0, left:3, right:0, height:44, display:'flex', alignItems:'center', padding:'0 12px', borderBottom:'1px solid rgba(201,168,76,0.12)', gap:8 }}>
                <span style={{ fontFamily:"'Bebas Neue', sans-serif", fontSize:16, letterSpacing:4, background:'linear-gradient(135deg,#F0D080,#C9A84C)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>100% JOHNNY</span>
                <span style={{ fontSize:6, letterSpacing:2, color:'rgba(201,168,76,0.3)', textTransform:'uppercase' }}>· Carte Membre Officielle</span>
                <div style={{ marginLeft:'auto', background:'rgba(192,40,42,0.25)', border:'1px solid rgba(192,40,42,0.4)', borderRadius:3, padding:'2px 7px', fontSize:6, letterSpacing:2, color:'rgba(255,255,255,0.6)', textTransform:'uppercase' }}>{validite.replace(' — ',' · ')}</div>
              </div>
              <div style={{ position:'absolute', top:44, left:3, right:0, bottom:32, display:'flex', alignItems:'center', padding:'0 12px' }}>
                <div style={{ flexShrink:0, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                  <a href="https://links-johnnysjh.web.app/" target="_blank" rel="noreferrer" style={{ display:'block', textDecoration:'none' }}>
                    <div style={{ width:60, height:60, background:'#fff', borderRadius:5, padding:3, boxShadow:'0 0 0 1px rgba(201,168,76,0.25)', overflow:'hidden' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 31 31" shapeRendering="crispEdges" style={{ width:'100%', height:'100%', display:'block' }}>
                        <rect width="31" height="31" fill="#ffffff"/>
                        <path stroke="#111111" d="M1 1.5h7m1 0h3m1 0h4m1 0h4m1 0h7M1 2.5h1m5 0h1m1 0h2m1 0h1m1 0h1m2 0h2m2 0h1m1 0h1m5 0h1M1 3.5h1m1 0h3m1 0h1m3 0h1m1 0h1m3 0h1m5 0h1m1 0h3m1 0h1M1 4.5h1m1 0h3m1 0h1m1 0h1m1 0h1m3 0h2m1 0h3m2 0h1m1 0h3m1 0h1M1 5.5h1m1 0h3m1 0h1m4 0h1m2 0h1m1 0h1m2 0h1m2 0h1m1 0h3m1 0h1M1 6.5h1m5 0h1m3 0h2m1 0h1m1 0h6m1 0h1m5 0h1M1 7.5h7m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h7"/>
                      </svg>
                    </div>
                  </a>
                  <div style={{ fontSize:5, letterSpacing:2, color:'rgba(201,168,76,0.3)', textTransform:'uppercase' }}>Scanner</div>
                </div>
                <div style={{ width:1, height:'60%', background:'linear-gradient(180deg,transparent,rgba(201,168,76,0.2),transparent)', margin:'0 10px', flexShrink:0 }}/>
                <div style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'center', gap:8 }}>
                  {[
                    { label:'Asso',  value:'100% Johnny'              },
                    { label:'Web',   value:'100pour100johnny.com'      },
                    { label:'Email', value:'contact@johnnysjh.fr'      },
                    { label:'Tél',   value:'07 44 73 82 21'            },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display:'flex', alignItems:'center', gap:5 }}>
                      <div style={{ width:3, height:3, borderRadius:'50%', background:'#C9A84C', opacity:0.5, flexShrink:0 }}/>
                      <span style={{ fontSize:6, letterSpacing:2, color:'rgba(201,168,76,0.4)', textTransform:'uppercase', flexShrink:0 }}>{label}</span>
                      <span style={{ fontSize:6, color:'rgba(255,255,255,0.3)', flexShrink:0 }}>·</span>
                      <span style={{ fontSize:8, color:'rgba(255,255,255,0.6)', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ position:'absolute', bottom:0, left:3, right:0, height:32, borderTop:'1px solid rgba(201,168,76,0.08)', display:'flex', alignItems:'center', padding:'0 12px', gap:8 }}>
                <div style={{ flex:1, height:1, background:'linear-gradient(90deg,rgba(201,168,76,0.15),transparent)' }}/>
                <span style={{ fontSize:6, letterSpacing:2, color:'rgba(255,255,255,0.15)', textTransform:'uppercase', whiteSpace:'nowrap' }}>Carte personnelle non cessible</span>
                <div style={{ flex:1, height:1, background:'linear-gradient(270deg,rgba(201,168,76,0.15),transparent)' }}/>
              </div>
            </div>
          </div>
        </div>

        <div style={{ fontSize:'0.68rem', letterSpacing:'3px', color:'rgba(255,255,255,0.2)', textTransform:'uppercase', animation:'pulseHint 2s ease-in-out infinite' }}>
          Appuyer pour retourner ↻
        </div>

        {/* Bouton Google Wallet */}
        {adhesion ? (
          <button
            onClick={handleGoogleWallet}
            disabled={walletLoading}
            style={{
              display:'flex', alignItems:'center', gap:10,
              padding:'13px 24px', borderRadius:14, cursor: walletLoading ? 'not-allowed' : 'pointer',
              background: walletLoading ? 'rgba(26,115,232,0.5)' : 'linear-gradient(135deg,#1a73e8,#0d47a1)',
              border:'none', color:'#fff', fontWeight:700, fontSize:'0.9rem',
              boxShadow:'0 4px 16px rgba(26,115,232,0.4)',
              width:'100%', justifyContent:'center',
              opacity: walletLoading ? 0.7 : 1,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z"/>
            </svg>
            {walletLoading ? 'Génération...' : 'Ajouter à Google Wallet'}
          </button>
        ) : (
          <div style={{ fontSize:'0.78rem', color:'rgba(255,255,255,0.35)', textAlign:'center', padding:'10px 20px', background:'rgba(255,255,255,0.04)', borderRadius:10, border:'1px solid rgba(255,255,255,0.08)' }}>
            💳 Google Wallet disponible avec une adhésion active
          </div>
        )}
      </div>

      <style>{`
        @keyframes floatCard { 0%,100%{transform:translateY(0) rotateX(2deg) rotateY(-3deg)} 33%{transform:translateY(-8px) rotateX(-1deg) rotateY(3deg)} 66%{transform:translateY(-4px) rotateX(3deg) rotateY(-1deg)} }
        @keyframes holoShine { 0%,100%{filter:hue-rotate(0deg) brightness(1)} 50%{filter:hue-rotate(30deg) brightness(1.3)} }
        @keyframes pulseDot  { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes pulseHint { 0%,100%{opacity:0.4} 50%{opacity:0.8} }
      `}</style>
    </div>
  )
}

// ════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ════════════════════════════════════════════════════════
function ProfilePage({ currentUser, onLogout }) {
  const navigate = useNavigate()

  // ── Auth ─────────────────────────────────────────────
  const [authMode, setAuthMode]               = useState('login')
  const [googleLoading, setGoogleLoading] = useState(false)
  const [email, setEmail]                     = useState('')
  const [password, setPassword]               = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [displayName, setDisplayName]         = useState('')
  const [showPassword, setShowPassword]       = useState(false)
  const [isAuthenticating, setIsAuthenticating] = useState(false)

  // ── Profil & UI ──────────────────────────────────────
  const [profileTab, setProfileTab] = useState('stats')
  const [compteTab, setCompteTab]   = useState('abonnement')
  const [userProfile, setUserProfile] = useState({ totalListenTime:0, badges:[], streak:0, sessionsCount:0, recentTops:[] })
  const [userFavorites, setUserFavorites]       = useState([])
  const [loadingFavorites, setLoadingFavorites] = useState(true)

  // ── Statistiques du profil (temps d'écoute, streak, sessions, badges) ──
  // Deux bugs corrigés ici :
  // 1) userProfile n'était jamais mis à jour après son état initial à
  //    zéro (onSnapshot manquant).
  // 2) Le vrai historique de temps d'écoute vit parfois sous un nom de
  //    champ différent (totalListeningTime / totalSecondsWatched,
  //    écrits par un autre système) plutôt que totalListenTime — comme
  //    déjà géré dans ClassementPage.jsx, on prend le maximum des trois.
  useEffect(() => {
    if (!currentUser) {
      setUserProfile({ totalListenTime:0, badges:[], streak:0, sessionsCount:0, recentTops:[] })
      return undefined
    }

    const unsubscribe = onSnapshot(
      doc(db, 'users', currentUser.uid),
      snapshot => {
        if (!snapshot.exists()) return

        const data = snapshot.data()

        const totalListenTime = Math.max(
          Number(data.totalListenTime || 0),
          Number(data.totalListeningTime || 0),
          Number(data.totalSecondsWatched || 0),
          Number(data.stats?.totalSecondsWatched || 0)
        )

        setUserProfile({
          totalListenTime,
          badges: data.badges || [],
          streak: data.streak || data.currentStreak || data.stats?.currentStreak || 0,
          sessionsCount: data.sessionsCount || data.sessions || data.stats?.sessionsCount || 0,
          recentTops: data.recentTops || []
        })
      },
      error => {
        console.error('Erreur chargement profil:', error)
      }
    )

    return () => unsubscribe()
  }, [currentUser])

  // ── Adhésion ─────────────────────────────────────────
  const [adhesion, setAdhesion]         = useState(null)
  const [allAdhesions, setAllAdhesions] = useState([])
  const [loadingAdhesion, setLoadingAdhesion] = useState(true)
  const [assorockProfileFallback, setAssorockProfileFallback] = useState(null) // { prenom, nom }

  // ── Carte ────────────────────────────────────────────
  const [showCarte, setShowCarte] = useState(false)

  // ── Suppression ──────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletePassword, setDeletePassword]   = useState('')
  const [deletingAccount, setDeletingAccount] = useState(false)

  const tabsRef = useRef(null)
  const tabButtonRefs = useRef([])
  const [activeDot, setActiveDot] = useState(0)
  const [isTVLayout, setIsTVLayout] = useState(() =>
    typeof window !== 'undefined' &&
    window.innerWidth >= 1280 &&
    !('ontouchstart' in window)
  )

  // Nom affiché — repli : displayName Firebase Auth → prénom/nom connu côté
  // AssoRock (renvoyé par get-membership-by-email) → partie locale de l'email.
  const memberDisplayName = (
    currentUser?.displayName
    || (assorockProfileFallback && (assorockProfileFallback.prenom || assorockProfileFallback.nom)
        ? `${assorockProfileFallback.prenom || ''} ${assorockProfileFallback.nom || ''}`.trim()
        : null)
    || currentUser?.email?.split('@')[0]
    || 'MEMBRE'
  ).replace(/\s*\([^)]*\)/g, '').trim() || 'MEMBRE'

  const tabs = [
    { id:'stats',  icon:<TrendingUp size={20}/>, label:'Statistiques' },
    { id:'badges', icon:<Award      size={20}/>, label:'Badges'       },
    { id:'tops',   icon:<Music      size={20}/>, label:'Mes TOPs'     },
    { id:'favs',   icon:<Heart      size={20}/>, label:'Favoris'      },
    { id:'compte', icon:<Settings   size={20}/>, label:'Compte'       },
  ]


  // ── Auth email ───────────────────────────────────────
  // ── Connexion Google ─────────────────────────────────
  const handleGoogleLogin = async () => {
    if (googleLoading) return
    setGoogleLoading(true)
    try {
      const user = await signInWithGoogleAnywhere()
      if (!user) return
      const snap = await getDocs(query(collection(db, 'users'), where('__name__', '==', user.uid)))
      if (snap.empty) {
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || (user.email || '').split('@')[0],
          photoURL: user.photoURL || null,
          totalListenTime: 0, badges: [], streak: 1, sessionsCount: 0, recentTops: [],
          createdAt: serverTimestamp(), lastVisit: serverTimestamp()
        })
        toast.success('✅ Compte créé avec Google')
      } else {
        toast.success('✅ Connexion Google réussie')
      }
    } catch (error) {
      console.error('Connexion Google :', error)
      const msg = googleErrorMessage(error)
      if (msg) toast.error(msg)
    } finally {
      setGoogleLoading(false)
    }
  }

  const handleAuth = async (e) => {
    e.preventDefault()
    setIsAuthenticating(true)
    try {
      if (authMode === 'reset') {
        await sendPasswordResetEmail(auth, email)
        toast.success('📧 Email de réinitialisation envoyé !')
        setAuthMode('login'); setEmail(''); return
      }
      if (authMode === 'signup') {
        if (password !== confirmPassword) { toast.error('❌ Mots de passe différents'); return }
        if (password.length < 6)         { toast.error('❌ Minimum 6 caractères'); return }
        const cred = await createUserWithEmailAndPassword(auth, email, password)
        const name = displayName.trim() || email.split('@')[0]
        await updateProfile(cred.user, { displayName:name })
        await setDoc(doc(db,'users',cred.user.uid), {
          uid:cred.user.uid, email, displayName:name,
          totalListenTime:0, badges:[], streak:1, sessionsCount:0, recentTops:[],
          createdAt:serverTimestamp(), lastVisit:serverTimestamp()
        })
        toast.success('✅ Compte créé !')
        setEmail(''); setPassword(''); setConfirmPassword(''); setDisplayName(''); return
      }
      await signInWithEmailAndPassword(auth, email, password)
      toast.success('✅ Connexion réussie !')
      setEmail(''); setPassword('')
    } catch (error) {
      const msgs = {
        'auth/user-not-found':'❌ Email introuvable',
        'auth/wrong-password':'❌ Mot de passe incorrect',
        'auth/invalid-credential':'❌ Email ou mot de passe incorrect',
        'auth/email-already-in-use':'❌ Email déjà utilisé',
        'auth/weak-password':'❌ Mot de passe trop faible',
        'auth/too-many-requests':'❌ Trop de tentatives.',
      }
      toast.error(msgs[error.code] || '❌ Erreur : ' + error.code)
    } finally { setIsAuthenticating(false) }
  }

  // ── Scroll tabs ──────────────────────────────────────
  useEffect(() => {
    const c = tabsRef.current; if (!c) return
    const onScroll = () => setActiveDot(Math.min(Math.round(c.scrollLeft / c.offsetWidth), 2))
    c.addEventListener('scroll', onScroll)
    return () => c.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const c = tabsRef.current; if (!c) return
    const el = c.querySelector('.profile-tab.active'); if (!el) return
    c.scrollTo({ left: el.offsetLeft - c.offsetWidth/2 + el.offsetWidth/2, behavior:'smooth' })
  }, [profileTab])

  // Navigation télécommande/clavier pour les grands écrans TV.
  useEffect(() => {
    const onResize = () => setIsTVLayout(
      window.innerWidth >= 1280 && !('ontouchstart' in window)
    )
    const onKeyDown = (e) => {
      if (!isTVLayout) return
      const active = document.activeElement
      if (active && ['INPUT','TEXTAREA','SELECT'].includes(active.tagName)) return
      if (!['ArrowLeft','ArrowRight'].includes(e.key)) return

      const current = tabs.findIndex(t => t.id === profileTab)
      if (current < 0) return
      const next = e.key === 'ArrowRight'
        ? Math.min(tabs.length - 1, current + 1)
        : Math.max(0, current - 1)

      if (next !== current) {
        e.preventDefault()
        setProfileTab(tabs[next].id)
        tabButtonRefs.current[next]?.focus()
      }
    }

    window.addEventListener('resize', onResize)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [isTVLayout, profileTab])

  // ── Adhésion — ancien système (accès direct + collectionGroup) puis
  // repli AssoRock (projet Firebase séparé, via Cloud Function) ─────────
  useEffect(() => {
    if (!currentUser) return
    ;(async () => {
      setLoadingAdhesion(true)
      let results = []

      // Tentative 1 : accès direct (chemin connu)
      try {
        const ASSO_ID = '2ir61kP6b6SdKPbWM4UR'
        const snap = await getDocs(
          collection(db, '100johnny', 'main', 'associations', ASSO_ID, 'adhesions')
        )
        results = snap.docs
          .map(d => ({ id: d.id, _path: d.ref.path, memberCardId: d.id, ...d.data() }))
          .filter(d => d.userId === currentUser.uid)
      } catch (e) {
        console.warn('Tentative 1 (accès direct) :', e.message)
      }

      // Tentative 2 : collectionGroup userId
      if (results.length === 0) {
        try {
          const snap = await getDocs(
            query(collectionGroup(db, 'adhesions'), where('userId', '==', currentUser.uid))
          )
          results = snap.docs.map(d => ({ id: d.id, _path: d.ref.path, memberCardId: d.id, ...d.data() }))
        } catch (e) {
          console.warn('Tentative 2 (collectionGroup userId) :', e.message)
        }
      }

      // Tentative 3 : collectionGroup userEmail
      if (results.length === 0) {
        try {
          const snap = await getDocs(
            query(collectionGroup(db, 'adhesions'), where('userEmail', '==', currentUser.email))
          )
          results = snap.docs.map(d => ({ id: d.id, _path: d.ref.path, memberCardId: d.id, ...d.data() }))
        } catch (e) {
          console.warn('Tentative 3 (collectionGroup userEmail) :', e.message)
        }
      }

      // Tentative 4 : sous-collection users/{uid}/adhesions
      if (results.length === 0) {
        try {
          const snap = await getDocs(collection(db, 'users', currentUser.uid, 'adhesions'))
          results = snap.docs.map(d => ({ id: d.id, _path: d.ref.path, memberCardId: d.id, ...d.data() }))
        } catch (e) {
          console.warn('Tentative 4 (users/uid/adhesions) :', e.message)
        }
      }

      // Tentative 5 : AssoRock (projet Firebase séparé), via la Cloud
      // Function pont — même principe que mon-compte.html. Le site n'a pas
      // de compte sur ce projet-là, donc ça passe par un compte de service
      // côté serveur.
      if (results.length === 0) {
        try {
          const idToken = await currentUser.getIdToken()
          const resp = await fetch(`${CLOUD_FUNCTIONS_BASE}/get-membership-by-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            }
          })
          const data = await resp.json()
          if (data.success && Array.isArray(data.adhesions)) {
            results = data.adhesions.map(sub => ({
              ...sub,
              // membre-verification.html / la carte/QR s'appuient sur
              // membreId (= userId AssoRock), jamais sur l'id du document.
              memberCardId: sub.membreId || sub.userId || sub.id
            }))
            if (data.profile && (data.profile.prenom || data.profile.nom)) {
              setAssorockProfileFallback(data.profile)
            }
          }
        } catch (e) {
          console.warn('Tentative 5 (AssoRock) :', e.message)
        }
      }

      results.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0))

      setAllAdhesions(results)
      setAdhesion(results.find(r => r.status === 'active') || null)
      setLoadingAdhesion(false)
    })()
  }, [currentUser])

  // ── Favoris ──────────────────────────────────────────
  useEffect(() => {
    if (!currentUser) return
    ;(async () => {
      setLoadingFavorites(true)
      try {
        const snap = await getDocs(query(
          collection(db, '100johnny', 'main', 'user_song_votes'),
          where('userId','==',currentUser.uid),
          where('vote','==','top')
        ))
        setUserFavorites(
          snap.docs.map(d => ({ id:d.id, ...d.data() }))
            .sort((a,b) => (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0))
        )
      } catch(e) { console.error(e) }
      finally { setLoadingFavorites(false) }
    })()
  }, [currentUser])

  // (Ancien second useEffect d'adhésions dupliqué — supprimé, fusionné
  // dans le premier bloc plus haut.)

  // ── Suppression compte ───────────────────────────────
  const handleDeleteAccount = async () => {
    if (!currentUser || !deletePassword) return
    setDeletingAccount(true)
    try {
      await reauthenticateWithCredential(currentUser, EmailAuthProvider.credential(currentUser.email, deletePassword))
      const uid = currentUser.uid
      await deleteDoc(doc(db,'users',uid))
      const v  = await getDocs(query(collection(db, '100johnny', 'main', 'user_song_votes'), where('userId','==',uid)))
      const d2 = await getDocs(query(collection(db, '100johnny', 'main', 'dedicaces'),       where('userId','==',uid)))
      await Promise.all([...v.docs.map(d => deleteDoc(d.ref)), ...d2.docs.map(d => deleteDoc(d.ref))])
      await deleteUser(currentUser)
      localStorage.clear()
      toast.success('✅ Compte supprimé')
      navigate('/')
    } catch(error) {
      if (['auth/wrong-password','auth/invalid-credential'].includes(error.code)) toast.error('❌ Mot de passe incorrect')
      else toast.error('❌ Erreur : ' + error.code)
    } finally { setDeletingAccount(false); setDeletePassword('') }
  }

  const formatTime = (s) => {
    const h = Math.floor(s/3600), m = Math.floor((s%3600)/60)
    return h > 0 ? `${h}h ${m}min` : `${m} min`
  }

  const allBadges = [
    { id:'first_listen',  name:'Première Écoute',  icon:'🎵', description:'Votre première chanson' },
    { id:'hour_listener', name:"1h d'Écoute",       icon:'⏰', description:"1 heure d'écoute"       },
    { id:'day_listener',  name:'Fan Quotidien',     icon:'📅', description:'24h cumulées'           },
    { id:'week_listener', name:'Mélomane',           icon:'🎸', description:"7 jours d'écoute"      },
    { id:'voter',         name:'Premier Vote',      icon:'👍', description:'Votre premier vote'     },
    { id:'top_voter',     name:'Critique Musical',  icon:'⭐', description:'10 votes'               },
    { id:'streak_7',      name:'Série de 7',        icon:'🔥', description:'7 jours consécutifs'   },
    { id:'night_owl',     name:'Oiseau de Nuit',    icon:'🦉', description:'Écoute après minuit'   },
  ]

  // ── FORMULAIRE AUTH ──────────────────────────────────
  if (!currentUser) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'linear-gradient(135deg,#0a0a0a 0%,#1a1a2e 100%)', padding:'20px' }}>
        <div style={{ width:'100%', maxWidth:'420px', background:'rgba(255,255,255,0.05)', borderRadius:'20px', padding:'35px 30px', border:'2px solid rgba(255,215,0,0.3)', boxShadow:'0 20px 60px rgba(0,0,0,0.6)' }}>
          <div style={{ textAlign:'center', marginBottom:'28px' }}>
            <div style={{ width:'75px', height:'75px', borderRadius:'50%', background:'linear-gradient(135deg,#ffd700,#ffed4e)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
              {authMode === 'reset' ? <KeyRound size={38} color="#000"/> : <User size={38} color="#000"/>}
            </div>
            <h2 style={{ color:'#ffd700', fontSize:'1.7rem', fontWeight:900, margin:'0 0 8px' }}>
              {authMode==='login' ? 'Connexion' : authMode==='signup' ? 'Créer un compte' : 'Mot de passe oublié'}
            </h2>
            <p style={{ color:'rgba(255,255,255,0.6)', fontSize:'0.9rem', margin:0 }}>
              {authMode==='login' ? 'Accédez à votre profil' : authMode==='signup' ? 'Rejoignez la communauté' : 'Recevez un lien par email'}
            </p>
          </div>

          {authMode !== 'reset' && (
            <>
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                style={{
                  width: '100%', minHeight: 54, padding: '0 10px', whiteSpace: 'nowrap',
                  background: '#fff', border: 0, borderRadius: 14,
                  color: '#1f1f1f', fontSize: '0.95rem', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  cursor: googleLoading ? 'default' : 'pointer',
                  opacity: googleLoading ? 0.7 : 1, marginBottom: 18
                }}
              >
                <GoogleIcon />
                {googleLoading ? 'Connexion en cours…' : 'Continuer avec Google'}
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.15)' }} />
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>ou avec votre e-mail</span>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.15)' }} />
              </div>
            </>
          )}

          <form onSubmit={handleAuth} style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
            {authMode === 'signup' && (
              <div>
                <label style={labelStyle}>Nom d'affichage</label>
                <div style={{ position:'relative' }}>
                  <UserPlus size={18} color="rgba(255,215,0,0.5)" style={iconStyle}/>
                  <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Votre prénom ou pseudo" style={inputStyle}/>
                </div>
              </div>
            )}
            <div>
              <label style={labelStyle}>Email</label>
              <div style={{ position:'relative' }}>
                <Mail size={18} color="rgba(255,215,0,0.5)" style={iconStyle}/>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="votre@email.com" required style={inputStyle}/>
              </div>
            </div>
            {authMode !== 'reset' && (
              <div>
                <label style={labelStyle}>Mot de passe</label>
                <div style={{ position:'relative' }}>
                  <Lock size={18} color="rgba(255,215,0,0.5)" style={iconStyle}/>
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required minLength={6} style={{ ...inputStyle, paddingRight:'45px' }}/>
                  <button type="button" onClick={() => setShowPassword(v => !v)} style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', padding:0 }}>
                    {showPassword ? <EyeOff size={18} color="rgba(255,255,255,0.4)"/> : <Eye size={18} color="rgba(255,255,255,0.4)"/>}
                  </button>
                </div>
              </div>
            )}
            {authMode === 'signup' && (
              <div>
                <label style={labelStyle}>Confirmer le mot de passe</label>
                <div style={{ position:'relative' }}>
                  <Lock size={18} color="rgba(255,215,0,0.5)" style={iconStyle}/>
                  <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" required minLength={6} style={inputStyle}/>
                </div>
              </div>
            )}
            <button type="submit" disabled={isAuthenticating} style={{ padding:'14px', background: isAuthenticating ? 'rgba(255,215,0,0.4)' : 'linear-gradient(135deg,#ffd700,#ffed4e)', border:'none', borderRadius:'12px', color:'#000', fontSize:'1rem', fontWeight:700, cursor: isAuthenticating ? 'not-allowed' : 'pointer', marginTop:'4px' }}>
              {isAuthenticating ? 'Chargement...' : authMode==='login' ? 'Se connecter' : authMode==='signup' ? 'Créer mon compte' : 'Envoyer le lien'}
            </button>
          </form>

          <div style={{ marginTop:'20px', textAlign:'center', display:'flex', flexDirection:'column', gap:'10px' }}>
            {authMode === 'login'  && (<><button onClick={() => { setAuthMode('reset'); setPassword('') }} style={linkGrey}>🔑 Mot de passe oublié ?</button><div style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.8rem' }}>──── ou ────</div><button onClick={() => { setAuthMode('signup'); setPassword(''); setConfirmPassword('') }} style={linkGold}>Créer un compte avec email</button></>)}
            {authMode === 'signup' && <button onClick={() => { setAuthMode('login'); setConfirmPassword('') }} style={linkGold}>Déjà un compte ? Se connecter</button>}
            {authMode === 'reset'  && <button onClick={() => setAuthMode('login')} style={linkGrey}>← Retour à la connexion</button>}
            <button onClick={() => navigate('/')} style={{ ...linkGrey, marginTop:'4px' }}>← Retour à l'accueil</button>
            <p style={{ margin:'10px 0 0', color:'rgba(255,255,255,0.35)', fontSize:'0.75rem', textAlign:'center' }}>{VERSION_LABEL}</p>
          </div>
        </div>
      </div>
    )
  }

  // ── PROFIL CONNECTÉ ──────────────────────────────────
  return (
    <>
      {showCarte && <CarteMembreModal adhesion={adhesion} currentUser={currentUser} memberDisplayName={memberDisplayName} onClose={() => setShowCarte(false)}/>}

      <div className={`profile-page ${isTVLayout ? "profile-page-tv" : ""}`}>
        <div className="profile-container">

          {/* Header */}
          <div className="profile-header">
            <div className="profile-avatar">
              {currentUser.photoURL
                ? <img src={currentUser.photoURL} alt="avatar" style={{ width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover' }}/>
                : <User size={48} color="#ffd700"/>
              }
            </div>
            <div className="profile-info">
              <h1 className="profile-name">{memberDisplayName}</h1>
              <p className="profile-email">{currentUser.email}</p>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6, flexWrap:'wrap' }}>

                {/* Badge membre */}
                {adhesion && (
                  <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:20, background:'rgba(255,215,0,0.12)', border:'1px solid rgba(255,215,0,0.35)', color:'#ffd700', fontSize:'0.7rem', fontWeight:700 }}>
                    <Star size={11} fill="#ffd700"/> MEMBRE ASSOCIATION
                  </span>
                )}

                {/* Bouton carte membre */}
                <button
                  onClick={() => setShowCarte(true)}
                  style={{
                    display:'inline-flex', alignItems:'center', gap:6,
                    padding:'4px 12px', borderRadius:20, cursor:'pointer',
                    background: adhesion ? 'linear-gradient(135deg,rgba(201,168,76,0.2),rgba(201,168,76,0.1))' : 'rgba(255,255,255,0.06)',
                    border: adhesion ? '1px solid rgba(201,168,76,0.5)' : '1px solid rgba(255,255,255,0.15)',
                    color: adhesion ? '#C9A84C' : 'rgba(255,255,255,0.4)',
                    fontSize:'0.7rem', fontWeight:700,
                  }}
                >
                  <CreditCard size={12}/>
                  Ma Carte Membre
                  {!adhesion && <span style={{ fontSize:'0.6rem', opacity:0.7 }}>· Adhésion requise</span>}
                </button>
              </div>
            </div>
            <button onClick={onLogout} className="btn-logout"><LogOut size={20}/><span>Déconnexion</span></button>
          </div>

          {/* Onglets */}
          <div className="tabs-wrapper">
            <div ref={tabsRef} className="profile-tabs">
              {tabs.map((tab, index) => (
                <button
                  key={tab.id}
                  ref={el => { tabButtonRefs.current[index] = el }}
                  aria-selected={profileTab===tab.id}
                  className={`profile-tab ${profileTab===tab.id ? 'active' : ''}`}
                  onClick={() => setProfileTab(tab.id)}
                  onFocus={() => setProfileTab(tab.id)}
                >
                  {tab.icon}<span>{tab.label}</span>
                </button>
              ))}
            </div>
            <div className="tabs-dots">
              {[0,1,2].map(i => <button key={i} className={`tab-dot ${activeDot===i ? 'active' : ''}`} onClick={() => { tabsRef.current?.scrollTo({ left:i*tabsRef.current.offsetWidth, behavior:'smooth' }); setActiveDot(i) }}/>)}
            </div>
          </div>

          {/* 🏆 CLASSEMENT — smartphone/tablette */}
          <button
            type="button"
            onClick={() => navigate('/classement')}
            aria-label="Ouvrir le classement"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              margin: '0 0 16px',
              padding: '14px 16px',
              borderRadius: 14,
              border: '1px solid rgba(255,215,0,0.28)',
              background: 'linear-gradient(135deg, rgba(255,215,0,0.12), rgba(255,215,0,0.04))',
              color: '#fff',
              cursor: 'pointer',
              textAlign: 'left',
              boxSizing: 'border-box',
            }}
          >
            <span
              style={{
                width: 42,
                height: 42,
                minWidth: 42,
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,215,0,0.12)',
                border: '1px solid rgba(255,215,0,0.25)',
                fontSize: 21,
              }}
            >
              🏆
            </span>

            <span style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  display: 'block',
                  color: '#ffd700',
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                }}
              >
                Communauté
              </span>

              <span
                style={{
                  display: 'block',
                  marginTop: 3,
                  color: '#fff',
                  fontSize: '0.95rem',
                  fontWeight: 800,
                }}
              >
                Classement
              </span>

              <span
                style={{
                  display: 'block',
                  marginTop: 2,
                  color: 'rgba(255,255,255,0.45)',
                  fontSize: '0.72rem',
                }}
              >
                Voir le classement des auditeurs
              </span>
            </span>

            <span
              aria-hidden="true"
              style={{
                color: '#ffd700',
                fontSize: 24,
                fontWeight: 300,
                lineHeight: 1,
              }}
            >
              ›
            </span>
          </button>

          <div className="profile-content">

            {/* STATS */}
            {profileTab === 'stats' && (
              <div className="profile-section">
                <div className="stats-grid">
                  {[
                    { icon:<Clock    size={32} color="#00d9ff"/>, value:formatTime(userProfile.totalListenTime), label:"Temps d'écoute", color:'#00d9ff', bg:'rgba(0,217,255,0.1)',   border:'rgba(0,217,255,0.3)'   },
                    { icon:<Flame    size={32} color="#ff1493"/>, value:userProfile.streak,                      label:'Jours de suite',  color:'#ff1493', bg:'rgba(255,20,147,0.1)',  border:'rgba(255,20,147,0.3)'  },
                    { icon:<Calendar size={32} color="#ffd700"/>, value:userProfile.sessionsCount,               label:'Sessions',        color:'#ffd700', bg:'rgba(255,215,0,0.1)',   border:'rgba(255,215,0,0.3)'   },
                    { icon:<Award    size={32} color="#00ff7f"/>, value:userProfile.badges.length,               label:'Badges',          color:'#00ff7f', bg:'rgba(0,255,127,0.1)',   border:'rgba(0,255,127,0.3)'   },
                  ].map((s,i) => (
                    <div key={i} className="stat-card" style={{ background:s.bg, border:`2px solid ${s.border}` }}>
                      <div className="stat-icon">{s.icon}</div>
                      <div className="stat-content">
                        <div className="stat-value" style={{ color:s.color }}>{s.value}</div>
                        <div className="stat-label">{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* BADGES */}
            {profileTab === 'badges' && (
              <div className="profile-section">
                <div className="badges-grid">
                  {allBadges.map(badge => {
                    const unlocked = userProfile.badges.includes(badge.id)
                    return (
                      <div key={badge.id} className={`badge-card ${unlocked ? 'unlocked' : 'locked'}`}>
                        <div className="badge-icon">{badge.icon}</div>
                        <div className="badge-info">
                          <div className="badge-name">{badge.name}</div>
                          <div className="badge-description">{badge.description}</div>
                        </div>
                        {unlocked && <Award size={20} color="#ffd700"/>}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* TOPS */}
            {profileTab === 'tops' && (
              <div className="profile-section">
                {userProfile.recentTops?.length > 0 ? (
                  <div className="tops-list">
                    {userProfile.recentTops.map((top,i) => (
                      <div key={i} className="top-item">
                        <div className="top-rank">#{i+1}</div>
                        <div className="top-cover">{top.cover ? <img src={top.cover} alt={top.title}/> : <Music size={28} color="rgba(255,215,0,0.4)"/>}</div>
                        <div className="top-info"><div className="top-title">{top.title}</div><div className="top-artist">{top.artist}</div></div>
                        <Heart size={20} color="#ff1493" fill="#ff1493"/>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state"><Music size={60} color="rgba(255,215,0,0.25)"/><p>Aucun TOP enregistré</p><p className="empty-hint">Votez pour vos chansons préférées !</p></div>
                )}
              </div>
            )}

            {/* FAVORIS */}
            {profileTab === 'favs' && (
              <div className="profile-section">
                {loadingFavorites ? (
                  <div className="loading-state"><div className="spinner"/><p>Chargement...</p></div>
                ) : userFavorites.length > 0 ? (
                  <div className="favs-list">
                    {userFavorites.map(fav => (
                      <div key={fav.id} className="fav-item">
                        <div className="fav-cover">{fav.cover ? <img src={fav.cover} alt={fav.songTitle}/> : <Music size={28} color="rgba(255,215,0,0.4)"/>}</div>
                        <div className="fav-info"><div className="fav-title">{fav.songTitle}</div><div className="fav-artist">{fav.songArtist}</div></div>
                        <Heart size={20} color="#ff1493" fill="#ff1493"/>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state"><Heart size={60} color="rgba(255,20,147,0.25)"/><p>Aucun favori</p><p className="empty-hint">Votez TOP pour vos chansons favorites !</p></div>
                )}
              </div>
            )}

            {/* COMPTE */}
            {profileTab === 'compte' && (
              <div className="profile-section">

                {/* Sous-nav */}
                <div style={{ display:'flex', gap:8, marginBottom:22, background:'rgba(255,255,255,0.04)', borderRadius:14, padding:5, border:'1px solid rgba(255,255,255,0.08)' }}>
                  {[
                    { id:'abonnement', icon:<CreditCard     size={16}/>, label:'Adhésion' },
                    { id:'danger',     icon:<AlertTriangle  size={16}/>, label:'Compte'   },
                  ].map(t => (
                    <button key={t.id} onClick={() => setCompteTab(t.id)} style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:7, padding:'10px 14px', borderRadius:10, cursor:'pointer', border:'none', fontWeight:700, fontSize:'0.85rem', transition:'all 0.18s', background: compteTab===t.id ? 'rgba(255,215,0,0.15)' : 'transparent', color: compteTab===t.id ? '#ffd700' : 'rgba(255,255,255,0.5)', boxShadow: compteTab===t.id ? 'inset 0 0 0 1px rgba(255,215,0,0.35)' : 'none' }}>
                      {t.icon}{t.label}
                    </button>
                  ))}
                </div>

                {/* ── ADHÉSION ── */}
                {compteTab === 'abonnement' && (
                  <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:38, height:38, borderRadius:11, background:'rgba(255,215,0,0.12)', border:'1px solid rgba(255,215,0,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                          <CreditCard size={20} color="#ffd700"/>
                        </div>
                        <div>
                          <div style={{ fontWeight:900, color:'#fff', fontSize:'1rem' }}>Mon Adhésion</div>
                          <div style={{ color:'rgba(255,255,255,0.42)', fontSize:'0.75rem' }}>Association Radio 100% Johnny</div>
                        </div>
                      </div>
                      {adhesion && (
                        <button onClick={() => setShowCarte(true)} style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'9px 16px', borderRadius:50, cursor:'pointer', background:'linear-gradient(135deg,rgba(201,168,76,0.25),rgba(201,168,76,0.12))', border:'1px solid rgba(201,168,76,0.5)', color:'#C9A84C', fontWeight:700, fontSize:'0.82rem' }}>
                          <CreditCard size={15}/> Voir ma carte
                        </button>
                      )}
                    </div>

                    {loadingAdhesion ? (
                      <div style={{ textAlign:'center', padding:'32px 0', color:'rgba(255,255,255,0.35)' }}>
                        <div style={{ fontSize:'1.6rem', marginBottom:10 }}>⏳</div>
                        Chargement de votre adhésion...
                      </div>

                    ) : allAdhesions.length === 0 ? (
                      <div style={{ textAlign:'center', padding:'28px 20px', background:'rgba(255,255,255,0.03)', borderRadius:16, border:'1px dashed rgba(255,255,255,0.12)' }}>
                        <div style={{ fontSize:'2.4rem', marginBottom:12 }}>🎸</div>
                        <div style={{ fontWeight:800, color:'#fff', fontSize:'1rem', marginBottom:6 }}>Vous n'êtes pas encore membre</div>
                        <div style={{ color:'rgba(255,255,255,0.42)', fontSize:'0.82rem', marginBottom:20 }}>Rejoignez l'association pour soutenir la légende de Johnny</div>
                        <button onClick={() => navigate('/abonnement')} style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'12px 24px', borderRadius:50, cursor:'pointer', background:'linear-gradient(135deg,#ffd700,#e6b800)', border:'none', color:'#000', fontWeight:800, fontSize:'0.9rem', boxShadow:'0 4px 16px rgba(255,215,0,0.35)' }}>
                          <CreditCard size={17}/> Adhérer — €20/an
                        </button>
                      </div>

                    ) : (
                      <>
                        <div style={{ fontSize:'0.72rem', fontWeight:700, color:'rgba(255,255,255,0.38)', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                          {allAdhesions.length} adhésion{allAdhesions.length>1?'s':''} trouvée{allAdhesions.length>1?'s':''}
                        </div>

                        {allAdhesions.map(sub => {
                          const endDateObj = sub.endDate ? (sub.endDate.toDate?.() || new Date(sub.endDate)) : null
                          const isExpired  = endDateObj ? endDateObj < new Date() : false
                          const daysLeft   = endDateObj ? Math.ceil((endDateObj - new Date()) / 86400000) : null

                          return (
                            <div key={sub.id} style={{ background:'linear-gradient(135deg,rgba(255,215,0,0.08) 0%,rgba(255,255,255,0.03) 100%)', border:`1.5px solid ${sub.status==='active' ? 'rgba(52,211,153,0.4)' : 'rgba(255,215,0,0.25)'}`, borderRadius:18, padding:'18px 20px', boxShadow: sub.status==='active' ? '0 4px 24px rgba(52,211,153,0.08)' : '0 4px 24px rgba(255,215,0,0.05)' }}>

                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
                                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                  <div style={{ width:42, height:42, borderRadius:12, background:'rgba(255,215,0,0.12)', border:'1px solid rgba(255,215,0,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.4rem' }}>🎸</div>
                                  <div>
                                    <div style={{ fontWeight:900, color:'#fff', fontSize:'0.9rem' }}>Adhésion 100% Johnny</div>
                                    <div style={{ color:'rgba(255,255,255,0.4)', fontSize:'0.72rem', marginTop:2 }}>{sub.type==='annuelle' ? 'Annuelle' : (sub.type || 'Annuelle')}</div>
                                  </div>
                                </div>
                                <SubStatusBadge status={isExpired ? 'expired' : (sub.status || 'pending')}/>
                              </div>

                              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px 14px' }}>
                                {[
                                  { label:'N° Membre', value:'JH·'+(sub.memberCardId || sub.id).substring(0,8).toUpperCase(), mono:true  },
                                  { label:'Montant',   value: sub.totalPrice || sub.subscriptionPrice ? `€${sub.totalPrice || sub.subscriptionPrice}` : '—' },
                                  { label:'Début',     value:fmtDate(sub.startDate) },
                                  { label:'Expire le', value:fmtDate(sub.endDate), warn:isExpired },
                                  { label:'Paiement',  value: sub.paymentMethod==='manual' ? 'Manuel' : sub.paymentMethod==='paypal' ? 'PayPal' : (sub.paymentMethod||'—') },
                                  { label:'Créée le',  value:fmtDate(sub.createdAt) },
                                ].map(({ label, value, mono, warn }) => (
                                  <div key={label}>
                                    <div style={{ fontSize:'0.65rem', fontWeight:700, color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:3 }}>{label}</div>
                                    <div style={{ fontSize: mono?'0.72rem':'0.82rem', fontWeight:700, color: warn?'#ff6b6b':mono?'#ffd700':'#fff', fontFamily: mono?'monospace':'inherit' }}>{value}</div>
                                  </div>
                                ))}
                              </div>

                              {/* Barre progression */}
                              {sub.startDate && sub.endDate && (() => {
                                const start = (sub.startDate.toDate?.() || new Date(sub.startDate)).getTime()
                                const end   = (sub.endDate.toDate?.()   || new Date(sub.endDate)).getTime()
                                const pct   = Math.min(100, Math.max(0, Math.round(((Date.now()-start)/(end-start))*100)))
                                return (
                                  <div style={{ marginTop:14 }}>
                                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                                      <span style={{ fontSize:'0.68rem', color:'rgba(255,255,255,0.4)', fontWeight:600 }}>Validité écoulée</span>
                                      <span style={{ fontSize:'0.68rem', color: daysLeft!==null && daysLeft<30?'#ff6b6b':'#10b981', fontWeight:700 }}>
                                        {daysLeft!==null ? (daysLeft>0?`${daysLeft}j restants`:'Expiré') : '—'}
                                      </span>
                                    </div>
                                    <div style={{ height:6, borderRadius:3, background:'rgba(255,255,255,0.08)', overflow:'hidden' }}>
                                      <div style={{ height:'100%', width:`${pct}%`, borderRadius:3, background: pct>80?'linear-gradient(90deg,#10b981,#ff6b6b)':'linear-gradient(90deg,#10b981,#ffd700)' }}/>
                                    </div>
                                  </div>
                                )
                              })()}

                              {/* Alerte renouvellement */}
                              {daysLeft !== null && daysLeft <= 30 && (
                                <div style={{ marginTop:12, padding:'9px 13px', borderRadius:10, background: isExpired?'rgba(220,20,60,0.08)':'rgba(255,215,0,0.07)', border:`1px solid ${isExpired?'rgba(220,20,60,0.3)':'rgba(255,215,0,0.3)'}`, display:'flex', alignItems:'center', justifyContent:'space-between', gap:10 }}>
                                  <span style={{ fontSize:'0.75rem', color: isExpired?'#ff6b6b':'#ffd700', fontWeight:700 }}>{isExpired?'⚠️ Adhésion expirée':`⏰ Expire dans ${daysLeft}j`}</span>
                                  <button onClick={() => navigate('/abonnement')} style={{ padding:'6px 14px', borderRadius:50, cursor:'pointer', background:'linear-gradient(135deg,#ffd700,#e6b800)', border:'none', color:'#000', fontWeight:800, fontSize:'0.75rem' }}>Renouveler →</button>
                                </div>
                              )}

                              {/* Bouton Google Wallet dans la card */}
                              {sub.status === 'active' && (
                                <button
                                  onClick={() => setShowCarte(true)}
                                  style={{ marginTop:14, width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'11px', borderRadius:12, cursor:'pointer', background:'linear-gradient(135deg,rgba(26,115,232,0.2),rgba(13,71,161,0.2))', border:'1px solid rgba(26,115,232,0.4)', color:'#5b9cf6', fontWeight:700, fontSize:'0.85rem' }}
                                >
                                  <Wallet size={17}/> Voir ma carte & Google Wallet
                                </button>
                              )}
                            </div>
                          )
                        })}
                      </>
                    )}
                  </div>
                )}

                {/* DANGER ZONE */}
                {compteTab === 'danger' && (
                  <div className="danger-zone">
                    <div className="danger-header"><AlertTriangle size={28} color="#e74c3c"/><h3>Zone Dangereuse</h3></div>
                    <p className="danger-warning">La suppression est <strong>irréversible</strong>.</p>
                    <ul className="danger-list">
                      <li>Statistiques et temps d'écoute</li>
                      <li>Badges débloqués</li>
                      <li>Chansons favorites et votes</li>
                      <li>Historique complet</li>
                    </ul>
                    <button onClick={() => setShowDeleteModal(true)} className="btn-delete-account"><Trash2 size={20}/> Supprimer mon compte</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modal suppression */}
        {showDeleteModal && (
          <div className="modal-overlay">
            <div className="modal-content delete-modal">
              <div className="modal-header"><AlertTriangle size={52} color="#e74c3c"/><h2>Confirmer la suppression</h2><p>Action définitive et irréversible</p></div>
              <div className="modal-body">
                <p className="modal-label">Entrez votre mot de passe :</p>
                <input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} placeholder="Mot de passe" className="modal-input" autoFocus/>
              </div>
              <div className="modal-actions">
                <button onClick={() => { setShowDeleteModal(false); setDeletePassword('') }} className="btn-cancel">Annuler</button>
                <button onClick={handleDeleteAccount} disabled={deletingAccount || !deletePassword} className="btn-confirm-delete">
                  {deletingAccount ? 'Suppression...' : <><Trash2 size={18}/> Supprimer</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

// ── Styles inline ────────────────────────────────────────
const labelStyle = { display:'block', color:'rgba(255,255,255,0.8)', fontSize:'0.85rem', marginBottom:'6px', fontWeight:600 }
const iconStyle  = { position:'absolute', left:'14px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }
const inputStyle = { width:'100%', padding:'12px 12px 12px 44px', background:'rgba(255,255,255,0.08)', border:'1.5px solid rgba(255,255,255,0.2)', borderRadius:'10px', color:'#fff', fontSize:'0.95rem', outline:'none', boxSizing:'border-box' }
const linkGrey   = { background:'none', border:'none', color:'rgba(255,255,255,0.5)', fontSize:'0.85rem', cursor:'pointer', textDecoration:'underline' }
const linkGold   = { ...linkGrey, color:'#ffd700', fontSize:'0.9rem', fontWeight:700 }

export default ProfilePage